import ReplyIcon from "@mui/icons-material/Reply";
import { Box, Fade, List, ListItemButton, Pagination, Stack, Typography } from "@mui/material";
import { Dayjs } from "dayjs";
import { useReadMessage } from "../../../App/Queries/MessageQueries";
import { useCurrentUser } from "../../../App/Queries/UserQueries";
import { useAppSelector } from "../../../App/State/Hooks/reduxHooks";
import { selectClientValue } from "../../../App/State/Slices/SelectedClientSlice";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";
import { IMessageHistory } from "../../../App/Typings/MessageText";
import { getMessageDisplayType } from "../../../MessageComponents/FunctionMethods/MessageFunctions";
import MessageActionsMenu from "../../Components/MessageActionsMenu";
import { useMessagesContext } from "../../State/MessagesContext";

export default function EmailListing() {
  const { id: userId } = useCurrentUser();
  const { clientName } = useAppSelector(selectClientValue);
  const { getUserAdjustedDate, getUserAdjustedDateFromNow } = useUserProfileStore();
  const { mutateAsync: markMessageAsReadAsync } = useReadMessage();
  const {
    selectedCandidate,
    messages,
    messagePage,
    totalMessagePages,
    setSelectedMessageId,
    setMessageModalOpen,
    setMessagePage,
  } = useMessagesContext();

  const viewMessageDetail = async (id: string) => {
    setSelectedMessageId(id);
    setMessageModalOpen(true);
    await markMessageAsReadAsync({ messageIds: [id], userId });
  };

  return (
    <Fade in timeout={500}>
      <Stack direction="column" alignItems="stretch" sx={{ height: "100%" }}>
        {messages.length === 0 ? (
          <Box sx={{ textAlign: "center", margin: 10 }}>
            <Typography fontSize={16}>No messages to display.</Typography>
          </Box>
        ) : (
          <List sx={{ flexGrow: { lg: 1 } }} data-test="message-center-email-listing">
            {messages.map((message, index) => (
              <DisplayIndividualMessage
                key={`message-list-item-${index}`}
                message={message}
                index={index}
                clientName={clientName}
                viewMessageDetail={viewMessageDetail}
                getUserAdjustedDate={getUserAdjustedDate}
                getUserAdjustedDateFromNow={getUserAdjustedDateFromNow}
                selectedCandidateName={
                  selectedCandidate ? `${selectedCandidate.firstName} ${selectedCandidate.lastName}` : ""
                }
              />
            ))}
          </List>
        )}
        <Pagination
          sx={{ alignSelf: "center" }}
          size="large"
          count={totalMessagePages}
          onChange={(_ev, page) => setMessagePage(page)}
          page={messagePage}
        />
      </Stack>
    </Fade>
  );
}

const DisplayIndividualMessage = ({
  message,
  index,
  clientName,
  viewMessageDetail,
  getUserAdjustedDate,
  getUserAdjustedDateFromNow,
  selectedCandidateName,
}: {
  message: IMessageHistory;
  index: number;
  clientName: string;
  viewMessageDetail: (id: string) => Promise<void>;
  getUserAdjustedDate: (date: string | Date | Dayjs) => Dayjs;
  getUserAdjustedDateFromNow: (date: string | Date | Dayjs) => string;
  selectedCandidateName: string;
}) => {
  const getDisplayName = (type: string, message: IMessageHistory) => {
    if (type === "reply") {
      return selectedCandidateName;
    }

    if (message.userName.toLowerCase() === "servergenerated" || message.userName.trim() === "") {
      return clientName;
    }

    return message.userName;
  };
  const type = getMessageDisplayType(message.type);
  return (
    <ListItemButton
      sx={{
        border: "1px solid #ccc",
        borderRadius: 1,
        margin: "12px",
        backgroundColor: "#fff",
        borderLeft: !message.hasBeenRead ? "4px solid" : "1px solid",
        borderLeftColor: message.hasBeenRead ? "#ccc" : "captivatePrimary.main",
      }}
      data-test={`message-list-item-${index}`}
      onClick={() => viewMessageDetail(message.messageId)}
      disableRipple={true}
    >
      <Stack direction="row" sx={{ width: "100%" }} justifyContent={"space-between"}>
        <Stack direction="row" spacing={2} sx={{ height: 50, marginLeft: message.hasBeenRead ? 0 : "-4px" }}>
          <Stack direction="row" spacing={2} sx={{ width: 400, justifySelf: "center", alignSelf: "center" }}>
            {getMessageDisplayType(message.type) === "reply" ? (
              <ReplyIcon sx={{ color: message.hasBeenRead ? "#ccc" : "inherit" }} />
            ) : (
              <></>
            )}
            <Typography
              sx={{
                fontWeight: message.hasBeenRead ? 200 : 500,
                wordBreak: "break-word",
                alignSelf: "end",
              }}
            >
              {getDisplayName(type, message)}
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontWeight: message.hasBeenRead ? 200 : 500,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {message.messageDisplay}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Typography
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
            fontSize={12}
          >
            {`${getUserAdjustedDate(message.created).format(
              "DD MMM YYYY, h:mm a",
            )} (${getUserAdjustedDateFromNow(message.created)})`}
          </Typography>
          {message.messageType === "ReplyEmailMessage" && (
            <Box sx={{ alignContent: "center" }}>
              <MessageActionsMenu
                messageId={message.messageId}
                hasBeenRead={message.hasBeenRead}
                messageType="email"
              />
            </Box>
          )}
        </Stack>
      </Stack>
    </ListItemButton>
  );
};
